/**
 * NavigationBar - Left sidebar navigation for the Kira App Shell.
 *
 * Renders colorful pill-shaped nav items for Journal, Tasks, Calendar, Habits, Focus, Countdown, Notes.
 * Selecting Journal reveals it behind the PIN lock gate (see ModuleRouter / journalLockStore).
 * Each module has its own accent color. Supports keyboard navigation (Tab/Arrow keys).
 * 44x44px minimum touch targets with visible focus rings.
 *
 * @see Requirements 1.1, 1.3, 1.7, 14.1, 14.3
 */

import { useRef, useCallback, type KeyboardEvent } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { usePomodoroStore } from '@/stores/pomodoroStore';
import type { ModuleId } from '../../types/modules';

// ─── Module Definitions ──────────────────────────────────────────────────────

interface ModuleNavItem {
  id: ModuleId;
  label: string;
  icon: string;
  activeClass: string;
  accentColor: string;
}

const modules: ModuleNavItem[] = [
  { id: 'journal', label: 'Journal', icon: '\uD83D\uDCD6', activeClass: 'nav-active-journal', accentColor: 'from-amber-400 to-orange-500' },
  { id: 'tasks', label: 'Tasks', icon: '\u2713', activeClass: 'nav-active-tasks', accentColor: 'from-violet-400 to-purple-500' },
  { id: 'calendar', label: 'Calendar', icon: '\uD83D\uDCC5', activeClass: 'nav-active-calendar', accentColor: 'from-cyan-400 to-teal-500' },
  { id: 'habits', label: 'Habits', icon: '\uD83C\uDFAF', activeClass: 'nav-active-habits', accentColor: 'from-emerald-400 to-green-500' },
  { id: 'focus', label: 'Focus', icon: '\u23F1\uFE0F', activeClass: 'nav-active-focus', accentColor: 'from-pink-400 to-rose-500' },
  { id: 'countdown', label: 'Countdown', icon: '\u23F3', activeClass: 'nav-active-countdown', accentColor: 'from-indigo-400 to-blue-500' },
  { id: 'notes', label: 'Notes', icon: '\uD83D\uDCDD', activeClass: 'nav-active-notes', accentColor: 'from-sky-400 to-cyan-500' },
];

// ─── Pomodoro Mini Indicator ─────────────────────────────────────────────────

function PomodoroMiniIndicator() {
  const isRunning = usePomodoroStore((s) => s.isRunning);
  const remainingSeconds = usePomodoroStore((s) => s.remainingSeconds);

  if (!isRunning) return null;

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const display = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <span
      className="absolute -top-1 -right-1 min-w-[20px] px-1 h-5 flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[10px] font-semibold leading-none animate-pulse"
      aria-label={`Pomodoro timer: ${minutes} minutes ${seconds} seconds remaining`}
    >
      {display}
    </span>
  );
}

// ─── NavigationBar Component ─────────────────────────────────────────────────

export function NavigationBar() {
  const activeModule = useUIStore((s) => s.activeModule);
  const setActiveModule = useUIStore((s) => s.setActiveModule);
  const navItemsRef = useRef<(HTMLButtonElement | null)[]>([]);

  // Handle arrow key navigation between nav items
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
      let nextIndex: number | null = null;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (index + 1) % modules.length;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (index - 1 + modules.length) % modules.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = modules.length - 1;
      }

      if (nextIndex !== null) {
        navItemsRef.current[nextIndex]?.focus();
      }
    },
    []
  );

  const setNavItemRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      navItemsRef.current[index] = el;
    },
    []
  );

  return (
    <nav
      className="w-16 tablet:w-60 flex-shrink-0 flex flex-col py-6 gap-2 border-r border-white/5"
      style={{ background: 'rgba(28, 28, 40, 0.6)', backdropFilter: 'blur(20px)' }}
      aria-label="Module navigation"
    >
      {/* App brand / logo area */}
      <div className="px-4 pb-4 mb-2 border-b border-white/5" aria-hidden="true">
        <div className="flex items-center gap-3 min-h-[44px] justify-center tablet:justify-start">
          <span className="text-2xl" role="img" aria-hidden="true">
            &#x2728;
          </span>
          <span className="hidden tablet:block text-heading-3 font-extrabold text-white tracking-tight">
            Kira
          </span>
        </div>
      </div>

      {/* Navigation items */}
      <div className="flex flex-col gap-1 px-3" role="list">
        {modules.map((module, index) => {
          const isActive = activeModule === module.id;

          return (
            <button
              key={module.id}
              ref={setNavItemRef(index)}
              type="button"
              role="listitem"
              onClick={() => setActiveModule(module.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-current={isActive ? 'page' : undefined}
              aria-label={module.label}
              className={`
                relative flex items-center gap-3 min-h-11 min-w-11 px-3 py-2.5
                rounded-full transition-all duration-300
                focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none
                ${
                  isActive
                    ? `${module.activeClass} text-white font-medium`
                    : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                }
              `}
            >
              {/* Icon */}
              <span
                className="flex items-center justify-center w-7 h-7 text-lg shrink-0"
                aria-hidden="true"
              >
                {module.icon}
              </span>

              {/* Label (hidden on collapsed / mobile) */}
              <span className="hidden tablet:block text-sm truncate">
                {module.label}
              </span>

              {/* Pomodoro mini indicator on Focus item */}
              {module.id === 'focus' && <PomodoroMiniIndicator />}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
